import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ky from "@/shared/api";

import { Resource, PaginatedResource } from "@/shared/types.ts";
import {
  TariffCategoryEntity,
  TariffEntity,
} from "@/features/tariffs/types.ts";
export * from "./types.ts";

type CreateTariffRequest = {
  isActive: boolean;

  name: string;
  description: string;
  icon: number;
  image: string;
  sortIndex: number;
  tariffCategoryId: number;

  length: number;
  height: number;
  width: number;
  weight: number;
  fields: string[];

  costPriceOfAirShipment: number;
  packageId: number;
  additionalCostForFragileCargo: number;
};

export function useTariffs({ enabled = true }: { enabled?: boolean }) {
  return useQuery<PaginatedResource<TariffEntity[]>>({
    queryKey: ["tariffs"],
    queryFn: () =>
      ky.get<PaginatedResource<TariffEntity[]>>(`/api/v1/tariffs`).json(),
    enabled,
    initialData: { data: [], total: 0 } as PaginatedResource<TariffEntity[]>,
  });
}

export function useCreateTariff({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<TariffEntity>, Error, CreateTariffRequest>({
    mutationFn: (data: CreateTariffRequest) =>
      ky.post("/api/v1/tariffs", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["tariffs"] });
    },
  });
}

export function useLazyTariff({ tariffId }: { tariffId: string | number }) {
  return useQuery<Resource<TariffEntity>>({
    queryKey: ["tariffs", `tariffs_${tariffId}`],
    queryFn: () =>
      ky.get<Resource<TariffEntity>>(`/api/v1/tariffs/${tariffId}`).json(),
  });
}

type UpdateTariffRequest = {
  id: number;
} & CreateTariffRequest;

export function useUpdateTariff({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<TariffEntity>, Error, UpdateTariffRequest>({
    mutationFn: (data: UpdateTariffRequest) =>
      ky.put("/api/v1/tariffs", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["tariffs"] });
    },
  });
}

type DeleteTariff = {
  tariffId: number | string;
};

export function useDeleteTariff({
  onSuccess,
  tariffCategoryId,
}: {
  onSuccess?: () => void;
  tariffCategoryId: number;
}) {
  const queryClient = useQueryClient();

  return useMutation<Resource<TariffEntity>, Error, DeleteTariff>({
    mutationFn: (data: DeleteTariff) =>
      ky.delete(`/api/v1/tariffs/${data.tariffId}`, { json: data }).json(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          "tariffs",
          "tariffsCategories",
          `tariffsCategories_${tariffCategoryId}`,
        ],
      });
      onSuccess?.();
    },
  });
}

type CreateTariffCategoryRequest = {
  name: string;
  key: string;
  active: boolean;
  sortIndex: number;
};

export function useTariffCategories() {
  return useQuery<Resource<TariffCategoryEntity[]>>({
    queryKey: ["tariffsCategories"],
    queryFn: () =>
      ky<Resource<TariffCategoryEntity[]>>("/api/v1/tariffs/categories").json(),
    initialData: { data: [] } as { data: TariffCategoryEntity[] },
  });
}

export function useCreateTariffCategory({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffCategoryEntity>,
    Error,
    CreateTariffCategoryRequest
  >({
    mutationFn: (data: CreateTariffCategoryRequest) =>
      ky.post("/api/v1/tariffs/categories", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["tariffsCategories"] });
    },
  });
}

export function useGetTariffCategoryLazy({
  tariffCategoryId,
}: {
  tariffCategoryId: number | string;
}) {
  return useQuery<Resource<TariffCategoryEntity> | undefined>({
    queryKey: ["tariffsCategories", `tariffsCategories_${tariffCategoryId}`],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<TariffCategoryEntity> | undefined
        >(`/api/v1/tariffs/categories/${tariffCategoryId}`, { signal: ctx.signal })
        .json(),
    enabled: false,
  });
}

export function useGetTariffCategory({
  tariffCategoryId,
}: {
  tariffCategoryId: number | string;
}) {
  return useQuery<Resource<TariffCategoryEntity> | undefined>({
    queryKey: ["tariffsCategories", `tariffsCategories_${tariffCategoryId}`],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<TariffCategoryEntity> | undefined
        >(`/api/v1/tariffs/categories/${tariffCategoryId}`, { signal: ctx.signal })
        .json(),
  });
}

type UpdateTariffCategory = {
  id: number;
} & CreateTariffCategoryRequest;

export function useUpdateTariffCategory({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffCategoryEntity>,
    Error,
    UpdateTariffCategory
  >({
    mutationFn: (data: UpdateTariffCategory) =>
      ky.put("/api/v1/tariffs/categories", { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tariffsCategories"] });
      onSuccess?.();
    },
  });
}

type DeleteTariffCategory = {
  id: number;
};

export function useDeleteTariffCategory() {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffCategoryEntity>,
    Error,
    DeleteTariffCategory
  >({
    mutationFn: (data: DeleteTariffCategory) =>
      ky.delete(`/api/v1/tariffs/categories/${data.id}`, { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tariffsCategories"] });
    },
  });
}
